import Split from "./Split";
import { SplitContentNode, SplitContentNodeContent } from "./splitContentTypes";
import { SplitContentConfigRenderer } from "./renderer/SplitContentConfigRenderer";
import { SplitContentManualSplitDialog } from "./SplitContentManualSplitDialog";
import { handleManualSplit } from "./splitContentUtils";
import { SetStateAction } from "react";

interface Props {
  layout: SplitContentNode;
  layoutWidth: number;
  layoutHeight: number;
  onDragging: (nodeId: string, sizes: [number, number]) => void;
  renderExtraContentPerNode: (node: SplitContentNodeContent, depth: number) => React.ReactNode;
  mode: "edit" | "preview";
  manualSplitConfig?: {
    showManualSplitDialog: boolean;
    setLayout: (value: SetStateAction<SplitContentNode>) => void;
    onLayoutChange: (value: SplitContentNode) => void;
    manualSplitNode: SplitContentNodeContent;
    onCloseManualSplitDialog: () => void;
  };
}

const DimensionInfo = ({ width, height }: { width: number; height: number }) => {
  return (
    <div className="absolute top-0 left-0 text-red bg-muted px-2 py-1 text-xs" style={{ zIndex: 9 }}>
      {`${width.toFixed(1)} x ${height.toFixed(1)}px`}
    </div>
  );
};

export const SplitContentLayout: React.FC<Props> = ({
  layout,
  layoutWidth,
  layoutHeight,
  onDragging,
  renderExtraContentPerNode,
  mode,
  manualSplitConfig,
}) => {
  const isEditMode = mode === "edit";
  const renderSplitContentNode = (
    node: SplitContentNode,
    parentWidth: number,
    parentHeight: number,
    depth: number = 0
  ): React.ReactNode => {
    if (node.type === "content") {
      return (
        <>
          <div className="w-full h-full relative overflow-hidden">
            <SplitContentConfigRenderer node={node} />
            {renderExtraContentPerNode?.(node, depth)}
          </div>
          {manualSplitConfig && manualSplitConfig.showManualSplitDialog && (
            <SplitContentManualSplitDialog
              onClose={() => {
                manualSplitConfig.onCloseManualSplitDialog();
              }}
              onConfirm={(direction, dimension) => {
                manualSplitConfig.setLayout((prevLayout) => {
                  const newLayout = handleManualSplit(
                    prevLayout,
                    manualSplitConfig.manualSplitNode,
                    direction,
                    dimension,
                    parentWidth,
                    parentHeight
                  );
                  manualSplitConfig.onLayoutChange(newLayout);
                  return newLayout;
                });
                manualSplitConfig.onCloseManualSplitDialog();
              }}
              nodeWidth={parentWidth}
              nodeHeight={parentHeight}
            />
          )}
        </>
      );
    }

    const splitStyle: React.CSSProperties = {
      height: node.direction === "horizontal" ? "100%" : "100%",
      width: "100%",
      display: "flex",
      flex: 1,
    };

    return (
      <Split
        key={node.id}
        style={splitStyle}
        mode={node.direction}
        onDragging={(prevSize, nextSize) => onDragging(node.id, [prevSize, nextSize])}
        visible={isEditMode}
      >
        {node.children?.map((child) => {
          const childWidth = node.direction === "horizontal" ? (parentWidth * Number(child?.size)) / 100 : parentWidth;
          const childHeight = node.direction === "vertical" ? (parentHeight * Number(child?.size)) / 100 : parentHeight;

          return (
            <div
              key={child.id}
              style={{
                flex: child.size,
                height: "100%",
                minHeight: 0,
                minWidth: 0,
                position: "relative",
              }}
            >
              {isEditMode && <DimensionInfo width={childWidth} height={childHeight} />}
              {renderSplitContentNode(child, childWidth, childHeight, depth + 1)}
            </div>
          );
        })}
      </Split>
    );
  };

  return (
    <div className="w-full h-full">
      {isEditMode && <DimensionInfo width={layoutWidth} height={layoutHeight} />}
      {renderSplitContentNode(layout, layoutWidth, layoutHeight, 0)}
    </div>
  );
};
